import React, { SyntheticEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from '~/components/Card';
import { Search, formValsReducer } from '~/components/Search';
import {
    ClinicList,
    PCPList,
    SearchContext,
    SearchContextType,
    initialSearch,
    useSearchClinic,
    useSearchPCP,
    useSearchSort,
} from '~/services';
import { mapPCPs, prepareSearchQueryString, useDebounce } from '~/util';

export function SearchView() {
    const { clinicList, pcpList, searchValues, setSearchValues, resetPagination } =
        React.useContext(SearchContext) as SearchContextType;
    const { resetSort } = useSearchSort();
    const history = useHistory();
    const { clinic, setClinic } = useSearchClinic();
    const { PCP, setPCP } = useSearchPCP();
    const [_clinic, _setClinic] = React.useState(clinic);
    const [_PCP, _setPCP] = React.useState(PCP);
    const [_PCPList, _setPCPList] = React.useState(pcpList);
    const [formVals, setFormVals] = React.useReducer(formValsReducer, searchValues);

    // debounce changes to clinic/PCP to reduce gap-aggregate requests
    const debounceClinicPCPChange = useDebounce(
        React.useCallback(
            (updatedClinic: ClinicList, updatedPCP: PCPList) => {
                setClinic(updatedClinic);
                setPCP(updatedPCP);
            },
            [setClinic, setPCP]
        ),
        4000
    );

    React.useEffect(() => {
        debounceClinicPCPChange(_clinic, _PCP);
    }, [_clinic, _PCP, debounceClinicPCPChange]);

    React.useEffect(() => {
        // Update the PCP list any time the clinic changes
        _setPCPList(mapPCPs(_clinic !== null && _clinic.length > 0 ? _clinic : clinicList));
    }, [_clinic, clinicList]);

    React.useEffect(() => {
        // Update disenrolled search value without submitting to update pill counts on dashboard
        if (formVals.disenrolled !== searchValues.disenrolled) {
            setSearchValues((prev) => ({ ...prev, disenrolled: formVals.disenrolled }));
        }
    }, [formVals.disenrolled, setSearchValues, searchValues.disenrolled]);

    const clearFilters = React.useCallback(() => {
        _setClinic(null);
        _setPCP(null);
        setFormVals('clear');
        setSearchValues(initialSearch);
    }, [_setClinic, _setPCP, setFormVals, setSearchValues]);

    function submit(e: SyntheticEvent) {
        e.preventDefault();
        resetPagination();
        resetSort();
        setClinic(_clinic);
        setPCP(_PCP);
        setSearchValues({ ...formVals });
        history.push(
            `/patients?${prepareSearchQueryString({
                ...formVals,
                clinic: _clinic,
                PCP: _PCP,
            })}`
        );
    }

    return (
        <Card title="Patient Search">
            <Search
                formVals={formVals}
                setFormVals={setFormVals}
                clinic={_clinic}
                setClinic={_setClinic}
                PCP={_PCP}
                setPCP={_setPCP}
                pcpList={_PCPList}
                clearFilter={clearFilters}
            />
            <hr className="my-3" />
            <div className="text-end">
                <button type="button" className="btn btn-secondary me-2" onClick={clearFilters}>
                    Clear
                </button>
                <button
                    onClick={submit}
                    type="submit"
                    form="patient-search"
                    className="btn btn-primary"
                >
                    Search
                </button>
            </div>
        </Card>
    );
}
